import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs/Rx';
import { Store } from '@ngrx/store';

import * as fromRoot from '../store/reducers';
import { TestTakerActions, AppUiActions } from '../store/actions';
import { LoggerService } from './logger.service';
import { TestTakerService } from './test-taker.service';
import { TestContentService } from './test-content.service';
import { TimerService } from './timer.service';
import { KeyboardActivityService } from './keyboard-activity.service';
import { MyModal } from '../components/common/my-modal/my-modal.component';
import * as Enum from '../dtos/enums';
import { ConfigHelper } from './config.helper';
import * as ErrorMessage from '../errors/constants';

@Injectable()
export class ResponseService implements OnDestroy {
    responses$: Observable<PgRendererDto.InteractionResponse[]>;
    responses: PgRendererDto.InteractionResponse[];
    testTakerActivities$: Observable<PgRendererDto.TestTakerActivity[]>;
    testTakerActivities: PgRendererDto.TestTakerActivity[];

    private previouslyFlushedPayload: PgRendererDto.TestTakerResponse;
    private testTakerActivitiesSubscription: Subscription;
    private responsesSubscription: Subscription;
    private flushPayloadSubscription: Subscription;
    private flushIntervalSubscription: Subscription;
    private pingIntervalSubscription: Subscription;
    private endOfTestSubscription: Subscription;
    private speakingBlobData: PgRendererDto.SpeakingBlobData = null;
    private readonly flushIntervalInMilliseconds: number = 30000;
    private readonly pingIntervalInMilliseconds: number = 60000;

    constructor(
        private store: Store<fromRoot.State>,
        private myModal: MyModal,
        private loggerService: LoggerService,
        private testTakerService: TestTakerService,
        private testContentService: TestContentService,
        private keyboardActivityService: KeyboardActivityService,
        private timerService: TimerService
    ) {
        this.responses$ = this.store.select(fromRoot.getResponses);
        this.responsesSubscription = this.responses$.subscribe(responses => {
            this.responses = responses;
        });

        this.testTakerActivities$ = this.store.select(fromRoot.getTestTakerActivitiesCache);
        this.testTakerActivitiesSubscription = this.testTakerActivities$.subscribe(testTakerActivities => {
            this.testTakerActivities = testTakerActivities;
        });

        this.endOfTestSubscription = this.store.select(fromRoot.getTestNavigation).subscribe(testNavigation => {
            if (testNavigation.isEndOfTest) {
                this.clearIntervalSubscriptions();
            }
        });

        this.flushPayloadSubscription = this.store.select(fromRoot.getFlushPayload).subscribe(flushPayload => {
            if (this.testTakerService.testTakerInfo && flushPayload && (flushPayload.TestTakerActivities.length > 0 || !flushPayload.triggeredByInterval) && !this.timerService.isPaused()) {
                if (JSON.stringify(flushPayload) != JSON.stringify(this.previouslyFlushedPayload)) {
                    if (ConfigHelper.isTdpMode() && this.speakingBlobData != null && (this.speakingBlobData.blob == null || this.speakingBlobData.blob.size == 0)) {
                        this.loggerService.reportFatalError(ResponseService.name, "Speaking blob data is missing.");
                        this.store.dispatch(new AppUiActions.ShowGenericPage(
                            "Recording Error",
                            ErrorMessage.Response_Saving_Missing_Blobs,
                            true
                        ));
                        return;
                    }

                    this.previouslyFlushedPayload = flushPayload;
                    this.clearTestTakerActivities();
                    this.testTakerService.saveTestTakerResponse(flushPayload, this.speakingBlobData)
                        .retryWhen((errors) => {
                            return errors
                                .delay(2000)
                                .scan((errorCount, err) => {
                                    if (errorCount > 3) {
                                        throw err;
                                    }
                                    return errorCount + 1;
                                }, 0);
                        })
                        .take(1)
                        .subscribe(
                            success => {
                                this.clearSpeakingBlobData();
                                this.store.dispatch(new TestTakerActions.FlushPayloadSuccess(!flushPayload.triggeredByInterval));
                            },
                            error => {
                                this.loggerService.reportFatalError(ResponseService.name, error);
                                let title = "";
                                let message = "";
                                if (ConfigHelper.isIppMode()) {
                                    title = "Error";
                                    message = `<p>Please refresh to start the test again.</p>`;
                                } else {
                                    title = "Communication Error";
                                    message = ErrorMessage.Response_Saving_Server_Call;
                                }
                                this.store.dispatch(new AppUiActions.ShowGenericPage(
                                    title,
                                    message,
                                    true
                                ));
                            }
                        );
                }
            }
        });

        if (ConfigHelper.isTdpMode()) {
            this.flushIntervalSubscription = Observable.interval(this.flushIntervalInMilliseconds)
                .subscribe(x => {
                    this.flush(true);
                });

            this.pingIntervalSubscription = Observable.interval(this.pingIntervalInMilliseconds)
                .subscribe(x => {
                    if (this.testContentService.activeTestlet) {
                        this.testTakerService.ping({
                            ClientApplicationSessionId: this.testTakerService.testTakerInfo.ClientApplicationSessionId,
                            StationNumber: this.testTakerService.testTakerInfo.StationNumber,
                            TestSessionId: this.testTakerService.testTakerInfo.TestSessionId,
                            TestTakerId: this.testTakerService.testTakerInfo.TestTakerId
                        }).subscribe();
                    }
                }, error => {
                    this.loggerService.reportError(ResponseService.name, error);
                });
        }
    }

    clearIntervalSubscriptions() {
        if (this.flushIntervalSubscription) {
            this.flushIntervalSubscription.unsubscribe();
        }
        if (this.pingIntervalSubscription) {
            this.pingIntervalSubscription.unsubscribe();
        }
    }

    ngOnDestroy() {
        this.responsesSubscription.unsubscribe();
        this.testTakerActivitiesSubscription.unsubscribe();
        this.flushPayloadSubscription.unsubscribe();
        this.endOfTestSubscription.unsubscribe();
    }

    flush(triggeredByInterval: boolean): Promise<any> {
        if ((ConfigHelper.isTdpMode() || ConfigHelper.isIppMode()) && this.testContentService.activeTestlet && !this.timerService.isPaused()) {
            let interactions = this.testContentService.getReferencedInteractionsByTestlet(this.testContentService.activeTestlet);
            if (triggeredByInterval) {
                interactions = interactions.filter(i => i.SpeakingInteraction == null);
            }
            else {
                try {
                    this.persistKeyboardActivity(interactions);
                }
                catch (err) {
                    this.loggerService.reportFatalError("Failed to persist keyboard data", err);
                }
            }
            const testTakerResponse = this.getTestTakerResponse(interactions.map(i => i.InteractionId), triggeredByInterval);

            this.store.dispatch(new TestTakerActions.SetFlushPayload(testTakerResponse));
        }
        return Promise.resolve();
    }

    persistKeyboardActivity(interactions){
        if(interactions){
            var matchingInterations = interactions.filter(i => i.InteractionId == this.keyboardActivityService.getRecordingSessionId())
            if(matchingInterations.length > 0){
                var interactionId = this.keyboardActivityService.getRecordingSessionId();
                this.keyboardActivityService.stopRecording();
                this.keyboardActivityService.processKeyboardDataFile((blob) => {               
                    this.testTakerService.saveTestTakerKeyboardActivity(interactionId, blob).subscribe(
                            success => {
                            },
                            error => {
                            }
                        );;
                });
            }
        }
    }

    clearInteractionResponses(interactionId: number) {
        this.store.dispatch(new TestTakerActions.ClearResponses(interactionId));
    }

    setResponse(interaction: PgRendererDto.InteractionDefinition, key: string, value: string, text: string, hasMultiItems: boolean, 
        choiceDisplayOrder: number) {
        let response = this.getResponse(interaction);
        if (!response) {
            response = {
                InteractionId: interaction.InteractionId,
                Items: []
            };
        }
        if (hasMultiItems == true) {
            response.Items = [...response.Items.filter(i => i.Value != value), {
                Key: key,
                Value: value,
                Text: text,
                ChoiceDisplayOrder: choiceDisplayOrder
            }];
        } else {
            response.Items = [{
                Key: key,
                Value: value,
                Text: text,
                ChoiceDisplayOrder: choiceDisplayOrder
            }];
        }
        this.store.dispatch(new TestTakerActions.SetResponse(response));
        this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.SetResponse, null, value, null, interaction.InteractionId, null);
    }

    setSpeakingBlobData(interactionId: number, blob: Blob) {
        this.speakingBlobData = {
            interactionId: interactionId,
            blob: blob
        };
    }

    clearSpeakingBlobData() {
        this.speakingBlobData = null;
    }

    removeResponseItem(interactionId: number, value: string) {
        this.store.dispatch(new TestTakerActions.RemoveResponseItem(interactionId, value));
    }

    getResponse(interaction: PgRendererDto.InteractionDefinition): PgRendererDto.InteractionResponse {
        return this.responses.find(x => interaction.InteractionId == x.InteractionId);
    }

    hasResponseItem(interactionId: number, value: string): boolean {
        const response = this.responses.find(x => x.InteractionId == interactionId);
        return response && response.Items.findIndex(x => x.Value == value) >= 0;
    }

    isInteractionFullyAnswered(interaction: PgRendererDto.InteractionDefinition): boolean {
        const response = this.getResponse(interaction);
        switch (interaction.InteractionTypeId as any) {
            case Enum.InteractionTypeEnum.Choice:
                return response && response.Items.length >= interaction.ChoiceInteraction.MinChoices;
            case Enum.InteractionTypeEnum.InlineChoice:
                return response && response.Items.length > 0;
            case Enum.InteractionTypeEnum.TextEntry:
                if (response && response.Items.length > 0) {
                    const responseItem = response.Items[0];
                    return responseItem.Value && responseItem.Value.length > 0;
                }
                return false;
            case Enum.InteractionTypeEnum.ExtendedText:
                return this.meetMinWordCountRequirement(interaction.ExtendedTextInteraction, response);
            case Enum.InteractionTypeEnum.Hotspot:
                return response && response.Items.length >= interaction.HotspotInteraction.MinChoices;
            case Enum.InteractionTypeEnum.Match:
                if (response && interaction.MatchInteraction.SimpleMatchSets && interaction.MatchInteraction.SimpleMatchSets.length >= 2) {
                    return interaction.MatchInteraction.SimpleMatchSets[0].SimpleAssociableChoices.length == response.Items.length;
                }
                return false;
            case Enum.InteractionTypeEnum.Order:
            case Enum.InteractionTypeEnum.Speaking:
                return true;
            case Enum.InteractionTypeEnum.Associate:
                if (response) {
                    return interaction.AssociateInteraction.SimpleAssociableChoices.length == response.Items.length;
                }
                return false;
            default:
                break;
        }

        return false;
    }

    getTestTakerResponse(interactionIds: number[], triggeredByInterval: boolean): PgRendererDto.TdpTestTakerResponse {
        const responses = this.responses.filter(x => interactionIds.findIndex(interactionId => interactionId == x.InteractionId) >= 0);
        // insert blank responses for unanswered interactions
        interactionIds.forEach(interactionId => {
            const response = responses.find(r => r.InteractionId == interactionId);
            if (!response) {
                responses.push({ InteractionId: interactionId, Items: [] });
            }
        });

        const testTakerResponse: PgRendererDto.TdpTestTakerResponse = {
            ClientApplicationSessionId: this.testTakerService.testTakerInfo.ClientApplicationSessionId,
            CurrentTestletUsedSeconds: this.timerService.getElapsedTimeInSeconds(),
            InteractionResponses: responses,
            TestSessionId: this.testTakerService.testTakerInfo.TestSessionId,
            TestTakerActivities: this.testTakerActivities,
            TestTakerId: this.testTakerService.testTakerInfo.TestTakerId,
            triggeredByInterval: triggeredByInterval
        };

        return testTakerResponse;
    }

    clearTestTakerActivities() {
        this.store.dispatch(new TestTakerActions.ClearTestTakerActivities());
    }

    getUnansweredInteractionCountInAssessmentItem(item: PgRendererDto.AssessmentItem): number {
        const referencedInteractions = this.testContentService.getReferencedInteractions(item);
        let unAnsweredCount = 0;
        referencedInteractions.forEach(interaction => {
            if (!this.isInteractionFullyAnswered(interaction)) {
                unAnsweredCount++;
            }
        });
        return unAnsweredCount;
    }

    getUnansweredInteractionCountInTestlet(testlet: PgRendererDto.TestletDefinition): number {
        let unAnsweredCount = 0;
        testlet.AssessmentItems.forEach(item => {
            unAnsweredCount += this.getUnansweredInteractionCountInAssessmentItem(item);
        });
        return unAnsweredCount;
    }

    getUnansweredInteractionCountInItemGroup(testlet: PgRendererDto.TestletDefinition, groupNumber: number): number {
        let unAnsweredCount = 0;
        testlet.AssessmentItems.filter(x => x.GroupNumber == groupNumber).forEach(item => {
            unAnsweredCount += this.getUnansweredInteractionCountInAssessmentItem(item);
        });
        return unAnsweredCount;
    }

    meetMinWordCountRequirement(extendedTextInteraction: PgRendererDto.ExtendedTextInteraction, response: PgRendererDto.InteractionResponse): boolean {
        if (!extendedTextInteraction.MinStrings) {
            return true;
        }

        if (response && response.Items.length > 0) {
            const responseText = this.stripHtmlTags(response.Items[0].Text);
            const responseWordCount = this.getWordCount(responseText);
            return responseWordCount >= extendedTextInteraction.MinStrings;
        }

        return false;
    }

    private stripHtmlTags(textWithHtml: string): string {
        if (textWithHtml) {
            return textWithHtml.replace(/(<([^>]+)>)/ig, " ");
        }
        return "";
    }

    getWordCount(text: string): number {
        const regex = /\s+/gi;
        return (text && text.trim().length > 0) ? text.trim().replace(regex, ' ').split(' ').length : 0;
    }

}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/response.service.ts